import {
  assertNever,
  ProspectListColumnTarget,
  ProspectListStatus,
  type ProspectListBinding,
  type ProspectListPreviewResponse,
  type ProspectListRead,
  type ProspectListUndoPreview,
  type ProspectListUndoRead,
} from '@signalvote/shared';

/** Bindings → the finance mapping grid's target → column-index record (ignored columns left out). */
export function bindingsToAssignments(
  headers: string[],
  bindings: ProspectListBinding[],
): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  for (const b of bindings) {
    if (b.target !== ProspectListColumnTarget.Ignore) out[b.target] = headers.indexOf(b.sourceColumn);
  }
  return out;
}

/** The grid's record → one binding per column, in file order; a column no target claims is ignored. */
export function assignmentsToBindings(
  headers: string[],
  assignments: Record<string, number | null>,
): ProspectListBinding[] {
  return headers.map((sourceColumn, idx) => {
    const target = Object.keys(assignments).find((t) => assignments[t] === idx) as ProspectListColumnTarget | undefined;
    return { sourceColumn, target: target ?? ProspectListColumnTarget.Ignore };
  });
}

export interface GateLine {
  label: string;
  count: number;
  /** Whole percent of all rows. */
  percent: number;
  /** Under half the file — usually a column bound to the wrong thing. */
  low: boolean;
}

/**
 * The whole-file counts as the confirmation gate reads them. Names and ZIPs are what the upload needs,
 * so those two are flagged when under half the rows have them; phone and email are optional.
 */
export function gateLines(preview: ProspectListPreviewResponse): GateLine[] {
  const total = preview.totalRows;
  const percent = (n: number) => (total === 0 ? 0 : Math.round((n / total) * 100));
  const line = (label: string, count: number, needed: boolean): GateLine => ({
    label,
    count,
    percent: percent(count),
    low: needed && count * 2 < total,
  });

  return [
    line('Usable names', preview.rowsWithUsableName, true),
    line('Phone numbers', preview.rowsWithPhone, false),
    line('Email addresses', preview.rowsWithEmail, false),
    line('ZIP codes', preview.rowsWithZip, true),
    line('Names listed more than once', preview.inFileDuplicateNames, false),
  ];
}

/** True once an upload will change no further on its own. */
export function isTerminalStatus(status: ProspectListStatus): boolean {
  switch (status) {
    case ProspectListStatus.Pending:
    case ProspectListStatus.Importing:
      return false;
    case ProspectListStatus.Imported:
    case ProspectListStatus.Failed:
    case ProspectListStatus.Undone:
      return true;
    default:
      return assertNever(status);
  }
}

/** Splits a server-sent-events buffer into its complete `data:` events and the unfinished tail. */
export function readSseEvents<T = unknown>(buffer: string): { events: T[]; rest: string } {
  const chunks = buffer.split('\n\n');
  const rest = chunks.pop() ?? '';
  const events = chunks
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.startsWith('data: '))
    .map((chunk) => JSON.parse(chunk.slice('data: '.length)) as T);
  return { events, rest };
}

/** Undo is offered once an upload has stopped, and only until it is undone. */
export function canUndo(status: ProspectListStatus): boolean {
  switch (status) {
    case ProspectListStatus.Imported:
    case ProspectListStatus.Failed:
      return true;
    case ProspectListStatus.Pending:
    case ProspectListStatus.Importing:
    case ProspectListStatus.Undone:
      return false;
    default:
      return assertNever(status);
  }
}

/** Why an undo kept people, e.g. "2 with a commitment, 5 already called". */
export function keptReasons(counts: ProspectListUndoPreview | ProspectListUndoRead): string {
  return [
    [counts.keptWithCommitmentCount, 'with a commitment'] as const,
    [counts.keptWithCallAttemptCount, 'already called'] as const,
    [counts.keptOnAnotherListCount, 'on another list'] as const,
  ]
    .filter(([n]) => n > 0)
    .map(([n, reason]) => `${n.toLocaleString()} ${reason}`)
    .join(', ');
}

/**
 * A failed upload that saved some rows before it broke. Retrying carries on from there; uploading the
 * same people again as a new list would duplicate anyone without an email.
 */
export function failedPartway(list: ProspectListRead): boolean {
  return list.status === ProspectListStatus.Failed && list.rowsWatermark > 0;
}
